import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTenantBranding } from "../../api/hooks";

const TenantContext = createContext(null);

/**
 * TenantProvider — resolves the active tenant from the URL slug.
 *
 * URL convention (docs/05 §2):
 *   /{slug}/login
 *   /{slug}/registro
 *   /{slug}/app/...
 *
 * The slug is extracted from the first path segment.  Routes without a slug
 * (e.g. /registro-academia) leave slug=null and branding defaults to null.
 *
 * Exposes (via useTenant()):
 *   { slug, tenantName, branding, isLoading }
 */
function TenantProvider({ children }) {
  const { pathname } = useLocation();

  // Extract slug from first path segment — e.g. "/acme/login" → "acme"
  // Segments that are known non-slug public routes are excluded
  const slug = useMemo(() => {
    const PUBLIC_ROOTS = new Set(["registro-academia", "login", "register", "activate"]);
    const first = pathname.split("/").filter(Boolean)[0];
    if (!first || PUBLIC_ROOTS.has(first)) return null;
    return first;
  }, [pathname]);

  const { data: branding, isLoading } = useTenantBranding(slug);

  const value = {
    slug,
    tenantName: branding?.name ?? null,
    branding: branding ?? null,
    isLoading,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within <TenantProvider>");
  return ctx;
}

export { TenantProvider, useTenant, TenantContext };
